import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { upsertAnnotation as upsertDocAnnotation, setAnnotations as setGlobalAnnotations } from '../../../store/slices/documentsSlice'
import { selectLatestWorkForDocument } from '../../../store/slices/workSlice'
import { setActiveTab } from '../../../store/slices/tabsSlice'
import { selectDocument as selectAnalysisDocument, setAnalysis, initializePendingAnalysis } from '../../../store/slices/analysisSlice'
import type { KeyMetric, VerificationCategory, ValidationItem } from '../../../store/slices/analysisSlice'
import { AlertCircle, Loader2, FileSpreadsheet, Home } from 'lucide-react'
import { FeatureGuard } from '../../../hooks/useFeatures'
import WorkPanelWrapper from './WorkPanelWrapper'
import { functionsClient } from '../../../lib/functionsClient'
import type { Annotation, FieldValue } from '../../ai-viewer/eventing/types'
import type { ViewerProvider } from '../../ai-viewer/provider/types'
import type { ViewerEventBus } from '../../ai-viewer/eventing'
import type { SemanticExtraction, ExtractedField, BulkAction, RawExtractionData } from '../../ai-viewer/eventing/extraction-types'
import { buildSemanticExtraction } from '../../ai-viewer/utils/extraction-grouping'

// Dynamic imports for client-only components
const SimplePDFHighlighter = React.lazy(() => import('../../ai-viewer/SimplePDFHighlighter'))
const DraggableAnnotationEditor = React.lazy(() => import('./DraggableAnnotationEditor'))
const ExtractionPanel = React.lazy(() => import('../../ai-viewer/overlay/ExtractionPanel'))
const ExportModal = React.lazy(() => import('./ExportModal'))

interface EnhancedFileViewerProps {
  documentId: string
  localFileUrl?: string  // Optional local file URL for immediate rendering
}

export const EnhancedFileViewer: React.FC<EnhancedFileViewerProps> = ({ documentId, localFileUrl }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { workId: workIdFromUrl } = useParams<{ workId: string }>()
  
  // Get work item for this document
  const workItem = useAppSelector(state => selectLatestWorkForDocument(state, documentId))
  
  // Comprehensive logging
  console.group('🔍 EnhancedFileViewer - Component State')
  console.log('Props:', { documentId, localFileUrl })
  console.log('URL Params:', { workIdFromUrl })
  console.log('Work Item from Redux:', workItem)
  console.log('Work Item ID:', workItem?.id || 'NOT FOUND')
  console.log('Document ID:', documentId)
  console.log('Location:', location.pathname)
  console.groupEnd()
  
  const globalAnnotations = useAppSelector(state => state.documents.annotations)
  const globalFields = useAppSelector(state => state.documents.fields)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [annotations, setAnnotations] = useState<Annotation[]>(globalAnnotations)
  const [fields, setFields] = useState<FieldValue[]>(globalFields)
  const [provider, setProvider] = useState<ViewerProvider | null>(null)
  const [, setBus] = useState<ViewerEventBus | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [isDrawing, setIsDrawing] = useState(false)
  const [scale, setScale] = useState(1.5)
  const [numPages, setNumPages] = useState(0)
  const [semanticExtraction, setSemanticExtraction] = useState<SemanticExtraction | null>(null)
  const [highlightedAnnotations, setHighlightedAnnotations] = useState<Set<string>>(new Set())
  // Editor shows automatically when annotation is selected
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null)
  const [currentAnnotationIndex, setCurrentAnnotationIndex] = useState(0)
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false)
  const exportButtonRef = React.useRef<HTMLButtonElement>(null)
  // Using new viewer only now
  
  // Initialize state from URL params
  const urlPage = searchParams.get('page')
  const urlAnnotationId = searchParams.get('annotation')
  const urlEntityKey = searchParams.get('entity')
  const urlHighlight = searchParams.get('highlight')
  
  // Initialize currentPage from URL if present
  useEffect(() => {
    if (urlPage) {
      const page = parseInt(urlPage, 10)
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page)
      }
    }
  }, [urlPage])
  
  // Initialize selected annotation from URL if present
  useEffect(() => {
    if (urlAnnotationId && annotations.length > 0) {
      const annotation = annotations.find(a => a.id === urlAnnotationId)
      if (annotation) {
        setSelectedAnnotationId(urlAnnotationId)
        if (annotation.page) {
          setCurrentPage(annotation.page)
        }
      }
    }
  }, [urlAnnotationId, annotations])
  
  // Initialize highlighted annotations from URL if present
  useEffect(() => {
    if (urlHighlight) {
      const ids = urlHighlight.split(',')
      setHighlightedAnnotations(new Set(ids))
    }
  }, [urlHighlight])
  
  // Helper function to update URL params without full navigation
  const updateUrlParams = useCallback((params: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams)
    
    Object.entries(params).forEach(([key, value]) => {
      if (value === null) {
        newParams.delete(key)
      } else {
        newParams.set(key, value)
      }
    })
    
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])
  
  // Derive file info from work item
  const fileName = workItem?.subject?.fileName || 'Document'
  const isProcessing = workItem?.status === 'running' || workItem?.status === 'pending'
  const progress = workItem?.runtime?.progress || 0
  const progressMessage = workItem?.runtime?.message || 'Processing...'

  // Clear state when documentId changes (switching to a new document)
  useEffect(() => {
    console.log('EnhancedFileViewer: documentId changed, clearing state', { documentId })
    
    // Clear all document-specific state
    setAnnotations([])
    setFields([])
    setSemanticExtraction(null)
    setHighlightedAnnotations(new Set())
    setSelectedAnnotationId(null)
    setCurrentAnnotationIndex(0)
    setCurrentPage(1)
    setError(null)
    
    // Also clear Redux state for this document
    dispatch(setGlobalAnnotations([]))
    
    // Select this document for analysis display
    dispatch(selectAnalysisDocument(documentId))
    
    // Initialize pending analysis if workItem exists
    if (workItem?.id) {
      dispatch(initializePendingAnalysis({
        documentId,
        workId: workItem.id
      }))
    }
  }, [documentId, workItem?.id, dispatch])

  // Use local file URL immediately if available
  useEffect(() => {
    console.log('EnhancedFileViewer: localFileUrl effect', { localFileUrl, documentId })
    if (localFileUrl) {
      console.log('EnhancedFileViewer: Setting local file URL for immediate display')
      setDocumentUrl(localFileUrl)
      setLoading(false)  // Don't show loading for local file
    }
  }, [localFileUrl])

  // Sync local annotations state with global Redux state when it changes
  useEffect(() => {
    if (globalAnnotations && globalAnnotations.length > 0) {
      console.log('EnhancedFileViewer: Syncing annotations from Redux:', globalAnnotations.length)
      setAnnotations(globalAnnotations)
    }
  }, [globalAnnotations])
  
  // Sync page changes to URL (when user manually navigates)
  useEffect(() => {
    const currentUrlPage = searchParams.get('page')
    const pageStr = currentPage.toString()
    
    // Extract workId from current URL path
    const currentPath = window.location.pathname
    const pathWorkId = currentPath.match(/\/app\/work\/([^/?]+)/)?.[1]
    
    // Only update if URL path already has the right work context
    if (currentPage > 0 && currentUrlPage !== pageStr && pathWorkId === workIdFromUrl && !!workIdFromUrl) {
      updateUrlParams({ page: pageStr })
    }
  }, [currentPage, searchParams, updateUrlParams, workIdFromUrl])

  // Load document and extraction data
  useEffect(() => {
    // Prevent multiple simultaneous loads
    let isActive = true;
    
    const loadDocument = async () => {
      try {
        console.log('EnhancedFileViewer: loadDocument called', { 
          hasLocalFileUrl: !!localFileUrl, 
          hasWorkItem: !!workItem, 
          workItemId: workItem?.id,
          workItemStatus: workItem?.status 
        })
      
        
        if (!isActive) return;
        setLoading(true)
        setError(null)

        // Get document URL from backend
        const docResult = await functionsClient.getDocument(documentId)
        if (!isActive) return;
        if (!docResult.success) {
          throw new Error('Failed to load document')
        }
        setDocumentUrl(docResult.url)

        // Load annotations AND extraction data together
        if (workItem?.id) {
          try {
            const { getFirestore, collection, getDocs } = await import('firebase/firestore')
            const db = getFirestore()
            
            // STEP 1: Load annotations first
            console.group('📦 Loading Annotations')
            console.log('Work ID:', workItem.id)
            console.log('Subcollection Path:', `work/${workItem.id}/annotations`)
            
            const annotationsRef = collection(db, 'work', workItem.id, 'annotations')
            const annotationsSnapshot = await getDocs(annotationsRef)
            
            let loadedAnnotations: any[] = []
            if (!annotationsSnapshot.empty) {
              loadedAnnotations = annotationsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }))
              console.log('✅ Loaded', loadedAnnotations.length, 'annotations')
              console.log('Sample Annotation:', loadedAnnotations[0])
              
              // Update state
              setAnnotations(loadedAnnotations as any)
              dispatch(setGlobalAnnotations(loadedAnnotations as any))
            } else {
              console.warn('⚠️ No annotations found in subcollection')
            }
            console.groupEnd()
            
            // STEP 2: Get extraction data from Redux (use summary or legacy extractedData)
            console.group('📊 Using Extraction Data from Redux')
            console.log('Work ID:', workItem.id)
            console.log('Work Item Outputs:', workItem.outputs)
            
            // Try new summary format first, fallback to legacy extractedData
            let rawExtractionData: RawExtractionData | null = null
            
            if (workItem.outputs?.extractionSummary) {
              // Use new summary format (preferred)
              console.log('✅ Using extraction summary (new format)')
              rawExtractionData = {
                transactions: workItem.outputs.extractionSummary.transaction_sample || [],
                account_info: workItem.outputs.extractionSummary.account_info || {},
                statement_period: workItem.outputs.extractionSummary.statement_period || {}
              } as RawExtractionData
              console.log('Transactions (preview):', rawExtractionData.transactions.length)
              console.log('Total count:', workItem.outputs.extractionSummary.transaction_count)
            } else if (workItem.outputs?.extractedData) {
              // Fallback to legacy format
              console.log('⚠️ Using legacy extractedData format')
              rawExtractionData = workItem.outputs.extractedData
              console.log('Transactions:', rawExtractionData?.transactions?.length || 0)
            } else {
              console.warn('⚠️ No extraction data in work item outputs')
            }
            
            if (rawExtractionData) {
              console.log('Account Info:', !!rawExtractionData?.account_info)
              console.log('Statement Period:', !!rawExtractionData?.statement_period)
            }
            console.groupEnd()
            
            // STEP 3: Build semantic extraction (annotations are optional)
            if (rawExtractionData) {
              console.group('🔨 Building Semantic Extraction')
              console.log('Using', loadedAnnotations.length, 'annotations')
              console.log('With', rawExtractionData.transactions?.length || 0, 'transactions')
              
              const semantic = buildSemanticExtraction(
                workItem.id,
                documentId,
                rawExtractionData,
                loadedAnnotations  // May be empty, that's OK
              )
              setSemanticExtraction(semantic)
              
              console.log('✅ Semantic extraction ready!', {
                transactions: semantic.transactions.length,
                hasAccountInfo: !!semantic.account_info,
                hasStatementPeriod: !!semantic.statement_period,
                annotationCount: loadedAnnotations.length
              })
              console.groupEnd()
            } else {
              console.error('❌ Cannot build semantic extraction - no extraction data', {
                hasRawData: !!rawExtractionData,
                annotationCount: loadedAnnotations.length
              })
            }
          } catch (error) {
            console.error('❌ Error loading data:', error)
          }
        } else {
          console.log('EnhancedFileViewer: [NO LOCAL URL] No workItem.id available!', { workItem: !!workItem })
        }
      } catch (err: any) {
        console.error('Error loading document:', err)
        if (isActive) {
          setError(err.message || 'Failed to load document')
        }
      } finally {
        if (isActive) {
          setLoading(false)
        }
      }
    }

    // Only load if documentId is valid and not already loading
    if (documentId && documentId !== '__DEV_TEST__' && !documentId.startsWith('temp-')) {
      loadDocument()
    }
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isActive = false;
    }
  }, [documentId, workItem?.id, workItem?.outputs?.extractionSummary, workItem?.status, localFileUrl]) // Re-run when work completes

  // Auto-fetch annotations when work completes
  useEffect(() => {
    const fetchAnnotationsOnCompletion = async () => {
      // Only fetch if work just completed and we don't have annotations yet
      if (workItem?.status === 'completed' && workItem?.id && annotations.length === 0) {
        try {
          console.log('🔄 Work completed - fetching annotations...')
          
          const { getFirestore, collection, getDocs } = await import('firebase/firestore')
          const db = getFirestore()
          
          const annotationsRef = collection(db, 'work', workItem.id, 'annotations')
          const snapshot = await getDocs(annotationsRef)
          
          if (!snapshot.empty) {
            const loadedAnnotations = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            
            console.log('✅ Loaded', loadedAnnotations.length, 'annotations after completion')
            setAnnotations(loadedAnnotations as any)
            dispatch(setGlobalAnnotations(loadedAnnotations as any))
          }
        } catch (error) {
          console.error('Failed to fetch annotations on completion:', error)
        }
      }
    }
    
    fetchAnnotationsOnCompletion()
  }, [workItem?.status, workItem?.id, annotations.length, dispatch])
  
  // Populate analysis data when semantic extraction is complete
  useEffect(() => {
    if (semanticExtraction && workItem?.id && workItem.status === 'completed') {
      console.log('Building analysis data from semantic extraction and validation report')
      
      // Get validation data from work item outputs (from Temporal workflow)
      const validationReport = workItem?.outputs?.validationReport
      const statistics = validationReport?.statistics || {}
      const correctness = validationReport?.correctness || {}
      const validationResults = validationReport?.validation_results || {}
      
      console.log('Validation Report:', validationReport)
      console.log('Statistics:', statistics)
      console.log('Correctness:', correctness)
      console.log('Validation Results:', validationResults)
      
      // Build Key Metrics from validation statistics
      const keyMetrics: KeyMetric[] = [
        { metric: 'Accounts Count', value: statistics.accounts_count || 1 },
        { metric: 'Bank Info Present', value: statistics.bank_info_present ?? !!semanticExtraction.account_info },
        { metric: 'Company Info Present', value: statistics.company_info_present ?? true },
        { metric: 'Transaction Count', value: statistics.transaction_count || semanticExtraction.transactions.length }
      ]
      
      // Build Verification data (correctness percentages from validation)
      // The balances_percentage_match contains the actual calculated match percentage per account
      let balancesCorrectness = correctness.balances ?? 100
      if (correctness.balances_percentage_match) {
        // Extract first account's percentage if it's an object
        const balancesMatch = correctness.balances_percentage_match
        if (typeof balancesMatch === 'object') {
          const accountNumbers = Object.keys(balancesMatch)
          if (accountNumbers.length > 0) {
            balancesCorrectness = balancesMatch[accountNumbers[0]]
          }
        } else {
          balancesCorrectness = balancesMatch
        }
      }
      
      const verification: VerificationCategory[] = [
        { 
          category: 'Job Metadata', 
          correctness: correctness.job_metadata ?? 100
        },
        { 
          category: 'Balances Present', 
          correctness: correctness.balances ?? 100
        },
        { 
          category: 'Balance Calculation Match', 
          correctness: balancesCorrectness
        },
        { 
          category: 'Bank Info', 
          correctness: correctness.bank_info ?? 100
        },
        { 
          category: 'Company Info', 
          correctness: correctness.company_info ?? 100
        },
        { 
          category: 'Transactions', 
          correctness: correctness.transactions ?? 100
        },
        { 
          category: 'Statement Info', 
          correctness: correctness.statement_info ?? 100
        }
      ]
      
      // Build Validation data from validation_results
      // Order: Job Metadata, Structural checks, then Content checks
      const validation: ValidationItem[] = [
        {
          category: 'Job Metadata',
          status: validationResults.job_metadata?.status || 'PASSED',
          warnings: validationResults.job_metadata?.warnings || [],
          errors: validationResults.job_metadata?.errors || []
        },
        {
          category: 'Balances',
          status: validationResults.balances?.status || 'PASSED',
          warnings: validationResults.balances?.warnings || [],
          errors: validationResults.balances?.errors || []
        },
        {
          category: 'Calculated Balances',
          status: validationResults.calculated_balances?.status || 'PASSED',
          warnings: validationResults.calculated_balances?.warnings || [],
          errors: validationResults.calculated_balances?.errors || []
        },
        {
          category: 'Bank Info',
          status: validationResults.bank_info?.status || 'PASSED',
          warnings: validationResults.bank_info?.warnings || [],
          errors: validationResults.bank_info?.errors || []
        },
        {
          category: 'Company Info',
          status: validationResults.company_info?.status || 'PASSED',
          warnings: validationResults.company_info?.warnings || [],
          errors: validationResults.company_info?.errors || []
        },
        {
          category: 'Statement Info',
          status: validationResults.statement_info?.status || 'PASSED',
          warnings: validationResults.statement_info?.warnings || [],
          errors: validationResults.statement_info?.errors || []
        },
        {
          category: 'Transactions',
          status: validationResults.transactions?.status || 'PASSED',
          warnings: validationResults.transactions?.warnings || [],
          errors: validationResults.transactions?.errors || []
        }
      ]
      
      // Dispatch analysis data
      dispatch(setAnalysis({
        documentId,
        workId: workItem.id,
        status: 'complete',
        keyMetrics,
        verification,
        validation,
        createdAt: Date.now(),
        updatedAt: Date.now()
      }))
      
      console.group('✅ Analysis Data Dispatched to Redux')
      console.log('Document ID:', documentId)
      console.log('Work ID:', workItem.id)
      console.log('Key Metrics:', keyMetrics)
      console.log('Verification (Correctness):', verification)
      console.log('Validation (Status/Errors):', validation)
      console.table(verification.map(v => ({
        category: v.category,
        correctness: `${v.correctness.toFixed(1)}%`
      })))
      console.table(validation.map(v => ({
        category: v.category,
        status: v.status,
        hasErrors: v.errors.length > 0,
        hasWarnings: v.warnings.length > 0
      })))
      console.groupEnd()
    }
  }, [semanticExtraction, workItem?.id, workItem?.status, workItem?.outputs?.validationReport, documentId, dispatch])

  const handleAnnotationUpdate = useCallback((updated: Annotation) => {
    setAnnotations(prev => 
      prev.map(a => a.id === updated.id ? updated : a)
    )
    dispatch(upsertDocAnnotation(updated))
    
    // Select the annotation to show editor
    setSelectedAnnotationId(updated.id)
    
    // If annotation was accepted, update related fields
    if (updated.status === 'accepted') {
      const linkedFields = updated.links.map(l => l.entity_key)
      setFields(prev => 
        prev.map(f => 
          linkedFields.includes(f.key) 
            ? { ...f, status: 'verified' as const, confidence: Math.max(f.confidence || 0, 0.9) }
            : f
        )
      )
    }
  }, [dispatch])

  const handleAnnotationCreate = useCallback((created: Annotation) => {
    setAnnotations(prev => [...prev, created])
    dispatch(upsertDocAnnotation(created))
    
    // Select the new annotation to show editor
    setSelectedAnnotationId(created.id)
  }, [dispatch])

  const handleAnnotationDelete = useCallback((id: string) => {
    setAnnotations(prev => prev.filter(a => a.id !== id))
    // Note: You might want to add a delete action to the store
  }, [])

  // Extraction panel handlers
  const handleFieldClick = useCallback((field: ExtractedField) => {
    console.log('Field clicked:', field.label, field.value)
    // Highlight and select the first annotation for this field
    if (field.annotations.length > 0) {
      const firstAnnotation = field.annotations[0]
      const annotationIds = field.annotations.map(a => a.id)
      
      setSelectedAnnotationId(firstAnnotation.id)
      setHighlightedAnnotations(new Set(annotationIds))
      
      // Jump to the page with this annotation
      if (firstAnnotation.page) {
        setCurrentPage(firstAnnotation.page)
      }
      
      // Update URL to reflect state
      updateUrlParams({
        page: firstAnnotation.page?.toString() || null,
        annotation: firstAnnotation.id,
        highlight: annotationIds.join(','),
        entity: field.id
      })
    }
  }, [updateUrlParams])

  const handleEntityClick = useCallback((entityAnnotations: Annotation[]) => {
    console.log('Entity clicked, highlighting annotations:', entityAnnotations.length)
    const annotationIds = entityAnnotations.map(a => a.id)
    
    // Highlight all annotations for this entity
    setHighlightedAnnotations(new Set(annotationIds))
    
    // Jump to first annotation's page
    if (entityAnnotations.length > 0 && entityAnnotations[0].page) {
      setCurrentPage(entityAnnotations[0].page)
      
      // Update URL
      updateUrlParams({
        page: entityAnnotations[0].page.toString(),
        highlight: annotationIds.join(','),
        annotation: null  // Clear single annotation selection
      })
    }
  }, [updateUrlParams])

  const handleFieldUpdate = useCallback((field: ExtractedField, newValue: string) => {
    console.log('Field updated:', field.label, newValue)
    // TODO: Update the field value in Firestore
    // For now, just update local state
    setSemanticExtraction(prev => {
      if (!prev) return prev
      // Deep clone and update the specific field
      // This is simplified - you'd need proper immutable update logic
      return { ...prev, updated_at: new Date().toISOString() }
    })
  }, [])

  const handleBulkAction = useCallback((action: BulkAction) => {
    console.log('Bulk action:', action)
    
    switch (action) {
      case 'accept_all':
        // Accept all annotations
        setAnnotations(prev => 
          prev.map(a => ({ ...a, status: 'verified' as const }))
        )
        break
      
      case 'reject_all':
        // Reject all annotations
        setAnnotations(prev => 
          prev.map(a => ({ ...a, status: 'rejected' as const }))
        )
        break
      
      case 'accept_transactions':
        // Accept only transaction annotations
        setAnnotations(prev => 
          prev.map(a => {
            const isTransaction = a.links?.some(link => 
              link.entity_key?.startsWith('transaction_')
            )
            return isTransaction ? { ...a, status: 'verified' as const } : a
          })
        )
        break
      
      default:
        console.warn('Unknown bulk action:', action)
    }
  }, [])

  // Annotation image capture handled in editor component

  // Navigation functions for annotations across all pages
  const handleNextAnnotation = useCallback(() => {
    if (annotations.length > 0) {
      const nextIndex = (currentAnnotationIndex + 1) % annotations.length
      const nextAnnotation = annotations[nextIndex]
      
      setCurrentAnnotationIndex(nextIndex)
      setSelectedAnnotationId(nextAnnotation.id)
      
      // Navigate to the page if it's different
      if (nextAnnotation.rect.page !== currentPage) {
        setCurrentPage(nextAnnotation.rect.page)
      }
      
      // Update URL
      updateUrlParams({
        page: nextAnnotation.rect.page.toString(),
        annotation: nextAnnotation.id,
        highlight: nextAnnotation.id
      })
    }
  }, [annotations, currentAnnotationIndex, currentPage, updateUrlParams])

  const handlePrevAnnotation = useCallback(() => {
    if (annotations.length > 0) {
      const prevIndex = currentAnnotationIndex === 0 ? annotations.length - 1 : currentAnnotationIndex - 1
      const prevAnnotation = annotations[prevIndex]
      
      setCurrentAnnotationIndex(prevIndex)
      setSelectedAnnotationId(prevAnnotation.id)
      
      // Navigate to the page if it's different
      if (prevAnnotation.rect.page !== currentPage) {
        setCurrentPage(prevAnnotation.rect.page)
      }
      
      // Update URL
      updateUrlParams({
        page: prevAnnotation.rect.page.toString(),
        annotation: prevAnnotation.id,
        highlight: prevAnnotation.id
      })
    }
  }, [annotations, currentAnnotationIndex, currentPage, updateUrlParams])

  // Reset annotation index when annotations change
  useEffect(() => {
    if (annotations.length > 0 && currentAnnotationIndex >= annotations.length) {
      setCurrentAnnotationIndex(0)
    }
  }, [annotations.length, currentAnnotationIndex])

  // Keyboard shortcuts: B toggles BoxTool, Esc exits, arrow keys for navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault()
        setIsDrawing(v => !v)
      } else if (e.key === 'Escape') {
        if (isDrawing) {
          e.preventDefault()
          setIsDrawing(false)
        }
      } else if (e.key === 'ArrowLeft' ) {
        e.preventDefault()
        handlePrevAnnotation()
      } else if (e.key === 'ArrowRight' ) {
        e.preventDefault()
        handleNextAnnotation()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isDrawing, handlePrevAnnotation, handleNextAnnotation])

  const handlePdfReady = useCallback((pdfProvider: ViewerProvider, eventBus: ViewerEventBus) => {
    setProvider(pdfProvider)
    setBus(eventBus)
    
    // Subscribe to page changes
    eventBus.on('PageChanged', (event) => {
      setCurrentPage(event.page)
    })
    
    // Subscribe to snapshot events for AI review
    eventBus.on('SnapshotCaptured', async (event) => {
      console.log('Snapshot captured for AI review:', event)
      // Here you could send the snapshot to an AI service for review
    })
  }, [])

  const renderActions = () => {
    const hasAnnotations = annotations.length > 0
    
    return (
      <div className="flex items-center gap-2">
        {/* Annotation Navigation - Show all annotations across pages */}
        {false && hasAnnotations && (
          <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded">
            <button
              onClick={handlePrevAnnotation}
              className="p-1 hover:bg-gray-200 rounded"
              title="Previous annotation"
              disabled={annotations.length <= 1}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-xs text-gray-600 px-2">
              {currentAnnotationIndex + 1} / {annotations.length}
            </span>
            <button
              onClick={handleNextAnnotation}
              className="p-1 hover:bg-gray-200 rounded"
              title="Next annotation"
              disabled={annotations.length <= 1}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
        
        
        {/* Show All Annotations Button */}
        {/* {annotations.length > 0 && (
          <button
            onClick={() => {
              if (annotations.length > 0) {
                setCurrentAnnotationIndex(0)
                setSelectedAnnotationId(annotations[0].id)
                setCurrentPage(annotations[0].rect.page)
              }
            }}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50"
            title="Go to first annotation"
          >
            All ({annotations.length})
          </button>
        )} */}
        
        {/* Box Tool Button */}
        <FeatureGuard feature="boxTool">
          <button
            onClick={() => setIsDrawing(v => !v)}
            className={`flex items-center gap-1 px-3 py-1 text-sm border rounded ${isDrawing ? 'bg-orange-50 border-orange-400 text-orange-700' : 'bg-white border-gray-300 hover:bg-orange-50 hover:border-orange-300'}`}
            title={isDrawing ? 'Leave Box Tool (B or Esc)' : 'Enter Box Tool (B)'}
          >
            {isDrawing ? 'Box Tool: On' : 'Box Tool'}
          </button>
        </FeatureGuard>
        
        {/* Zoom Controls */}
        <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded">
          <button
            onClick={() => setScale(Math.max(0.5, scale - 0.25))}
            className="p-1 hover:bg-gray-200 rounded"
            title="Zoom out"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <span className="text-xs text-gray-600 px-2 min-w-[3rem] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => setScale(Math.min(3, scale + 0.25))}
            className="p-1 hover:bg-gray-200 rounded"
            title="Zoom in"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        
        {/* JSON Data Buttons - Removed old implementation */}
        
        {documentUrl && (
          <>
            {/* <a
              href={documentUrl}
              download={fileName}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-orange-50 hover:border-orange-300"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
             */}
            {workItem?.outputs?.excelUrl && (
              <button
                ref={exportButtonRef}
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 hover:bg-green-700 text-white rounded font-medium shadow-sm transition-colors"
                title="Export extracted data"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Export
              </button>
            )}
          </>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <WorkPanelWrapper title={fileName}>
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            <p className="text-sm text-gray-600">Loading document...</p>
          </div>
        </div>
      </WorkPanelWrapper>
    )
  }

  if (error) {
    return (
      <WorkPanelWrapper title={fileName}>
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-gray-600">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded hover:bg-orange-600"
            >
              Retry
            </button>
          </div>
        </div>
      </WorkPanelWrapper>
    )
  }

  return (
    <WorkPanelWrapper 
      title={fileName} 
      actions={renderActions()}
    >
      <div className="relative w-full h-full bg-gray-100 flex">
        {/* PDF Viewer Section - Flex grow */}
        <div className="flex-1 relative">
          {documentUrl && (
            <React.Suspense fallback={
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            }>
              <>
                <SimplePDFHighlighter
                url={documentUrl}
                annotations={annotations}
                onAnnotationCreate={handleAnnotationCreate}
                onAnnotationUpdate={handleAnnotationUpdate}
                onReady={handlePdfReady}
                onDocumentLoad={setNumPages}
                className="w-full h-full"
                currentPage={currentPage}
                isDrawing={isDrawing}
                scale={scale}
              />
              
              {/* Processing Modal Overlay */}
              {isProcessing && annotations.length === 0 && (
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-auto p-6 sm:p-8">
                    <div className="text-center">
                      {/* Processing Icon */}
                      <div className="mb-4 sm:mb-6">
                        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full">
                          <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 animate-spin" />
                        </div>
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                        Processing Document
                      </h3>
                      
                      {/* Progress */}
                      <div className="mb-3 sm:mb-4">
                        <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-2">
                          <span className="truncate mr-2">{progressMessage}</span>
                          <span className="font-medium flex-shrink-0">{progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Message */}
                      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                        The AI system is processing this document. You can track progress in the Activity bar on the right. 
                        Comprehensive extraction and analysis will take a few minutes. Meanwhile, you can return to the home tab or click here to start a new extraction.
                      </p>
                      
                      {/* Action Button */}
                      <button
                        onClick={() => {
                          dispatch(setActiveTab('home'))
                          navigate('/app')
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors text-sm sm:text-base"
                      >
                        <Home className="w-4 h-4 sm:w-5 sm:h-5" />
                        Go to Home
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          </React.Suspense>
        )}
        
        {/* Page Navigation Controls - Center Bottom */}
        {numPages > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded shadow-md px-3 py-1.5 flex items-center gap-2 z-20">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous page"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-600">Page</span>
              <input
                type="number"
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value, 10)
                  if (page >= 1 && page <= numPages) {
                    setCurrentPage(page)
                  }
                }}
                className="w-12 px-1.5 py-0.5 text-xs border border-gray-300 rounded text-center font-medium"
                min={1}
                max={numPages}
              />
              <span className="text-xs text-gray-500">of {numPages}</span>
            </div>
            
            <button
              onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
              disabled={currentPage >= numPages}
              className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next page"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Field Summary Panel */}
        {fields.length > 0 && (
          <div className="absolute bottom-4 left-4 max-w-sm bg-white rounded shadow-lg p-3">
            <h3 className="text-sm font-semibold mb-2">Extracted Fields</h3>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {fields.slice(0, 5).map(field => (
                <div key={field.key} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">{field.key}:</span>
                  <span className={`font-medium ${
                    field.status === 'verified' ? 'text-green-600' : 
                    field.status === 'rejected' ? 'text-red-600' : 
                    'text-gray-900'
                  }`}>
                    {typeof field.value === 'number' && field.unit === 'USD' 
                      ? `$${field.value.toFixed(2)}`
                      : String(field.value)
                    }
                    {field.confidence && (
                      <span className="ml-1 text-gray-400">
                        ({Math.round(field.confidence * 100)}%)
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
            {fields.length > 5 && (
              <p className="text-xs text-gray-500 mt-2">
                +{fields.length - 5} more fields
              </p>
            )}
          </div>
        )}
        
        {/* Enhanced Draggable Annotation Editor */}
        {selectedAnnotationId && (
          (() => {
            const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId)
            if (!selectedAnnotation) return null
            
            return (
              <React.Suspense fallback={null}>
                <DraggableAnnotationEditor
                  annotation={selectedAnnotation}
                  provider={provider}
                  onUpdate={handleAnnotationUpdate}
                  onDelete={handleAnnotationDelete}
                  onClose={() => setSelectedAnnotationId(null)}
                />
              </React.Suspense>
            )
          })()
        )}
        
        {/* Export Modal */}
        {showExportModal && workItem?.outputs?.excelUrl && (
          <React.Suspense fallback={null}>
            <ExportModal
              isOpen={showExportModal}
              onClose={() => setShowExportModal(false)}
              onDownload={() => {
                // The actual download is handled by the anchor tag in the modal
                console.log('Export download initiated')
              }}
              fileName={fileName}
              downloadUrl={workItem.outputs.excelUrl}
              buttonRef={exportButtonRef}
            />
          </React.Suspense>
        )}
        
        </div>
        {/* End PDF Viewer Section */}
        
        {/* Extraction Panel - Side by side */}
        <FeatureGuard feature="extractionPanel">
          <React.Suspense fallback={
            <div className="w-72 lg:w-80 xl:w-96 bg-white border-l border-gray-200 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          }>
            <ExtractionPanel
              extraction={semanticExtraction}
              onFieldClick={handleFieldClick}
              onEntityClick={handleEntityClick}
              onFieldUpdate={handleFieldUpdate}
              onBulkAction={handleBulkAction}
            />
          </React.Suspense>
        </FeatureGuard>
        
      </div>
    </WorkPanelWrapper>
  )
}
